/**
 * Line ranges in guide links, in the format of code_snippet_to_doc:
 * `<start>:<end>`, where each side is a line number, a `/glob/` matched
 * anywhere in a line, or an `r/regex/`. The end is searched for after the
 * start line and is excluded unless it ends with `+`. A colon inside a pattern
 * is written `\:`. A start alone is one line.
 */

export interface LineRange {
    /** 1-based, inclusive. */
    first: number;
    last: number;
}

/** The lines `spec` selects in `lines`, or why it selects none. */
export function resolveLines(spec: string, lines: readonly string[]): LineRange | string {
    const parts = spec.split(/(?<!\\):/);
    if (parts.length > 2 || parts[0] === "")
        return `"${spec}" is not a line range such as 12:20 or r/^void app_main/:r/^\\}/+.`;
    const first = find(parts[0]!, lines, 0);
    if (typeof first === "string") return first;
    if (parts.length === 1) return { first, last: first };
    const inclusive = parts[1]!.endsWith("+");
    const end = find(inclusive ? parts[1]!.slice(0, -1) : parts[1]!, lines, first);
    if (typeof end === "string") return end;
    const last = inclusive ? end : end - 1;
    if (last < first) return `"${spec}" ends before it starts.`;
    return { first, last };
}

/** The 1-based line `spec` gives, searching the lines after line `after`. */
function find(spec: string, lines: readonly string[], after: number): number | string {
    if (/^\d+$/.test(spec)) {
        const line = Number(spec);
        return line >= 1 && line <= lines.length ? line : `Line ${line} is past the end of the file.`;
    }
    let pattern: RegExp;
    if (/^r\/.*\/$/.test(spec)) {
        try {
            pattern = new RegExp(spec.slice(2, -1).replace(/\\:/g, ":"));
        } catch (e) {
            return `${spec} is not a valid regular expression: ${(e as Error).message}`;
        }
    } else if (/^\/.*\/$/.test(spec)) {
        pattern = globPattern(spec.slice(1, -1).replace(/\\:/g, ":"));
    } else {
        return `"${spec}" is not a line number, a /glob/ or an r/regex/.`;
    }
    for (let i = after; i < lines.length; i++) if (pattern.test(lines[i]!)) return i + 1;
    return `${spec} matches no line${after > 0 ? ` after line ${after}` : ""}.`;
}

/** A glob as fnmatch reads it (`*`, `?`, `[...]`, `[!...]`), matched anywhere in a line; `\x` is a literal x. */
function globPattern(glob: string): RegExp {
    let source = "";
    for (let i = 0; i < glob.length; i++) {
        const c = glob[i]!;
        const set = c === "[" ? /^\[(!?)([^\]]+)\]/.exec(glob.slice(i)) : null;
        if (c === "*") source += ".*";
        else if (c === "?") source += ".";
        else if (set !== null) {
            source += `[${set[1] === "!" ? "^" : ""}${set[2]!.replace(/[\\^]/g, "\\$&")}]`;
            i += set[0].length - 1;
        } else {
            const literal = c === "\\" && i + 1 < glob.length ? glob[++i]! : c;
            source += literal.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
        }
    }
    return new RegExp(source);
}
